import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SelectionKey } from '../components/data-table/data-source/data-source.types';

export type SelectionMode = 'none' | 'partial' | 'all';

export interface SelectionState {
  mode: SelectionMode;
  selectedIds: Set<SelectionKey>;
  excludedIds: Set<SelectionKey>;
}

export interface BulkOperationPayload {
  isAll: boolean;
  selectedIds: SelectionKey[];
  excludedIds: SelectionKey[];
}

@Injectable()
export class TableSelectionService<TRow = any> {
  private readonly state$$ = new BehaviorSubject<SelectionState>({
    mode: 'none',
    selectedIds: new Set(),
    excludedIds: new Set(),
  });
  
  private rowIdField!: keyof TRow;
  
  configure(rowIdField: keyof TRow): void {
    this.rowIdField = rowIdField;
  }
  
  get state(): Observable<SelectionState> {
    return this.state$$.asObservable();
  }
  
  get currentState(): SelectionState {
    return this.state$$.value;
  }
  
  select(row: TRow): void {
    const key = this.getRowKey(row);
    const state = this.currentState;
    
    if (state.mode === 'all') {
      state.excludedIds.delete(key);
    } else {
      state.selectedIds.add(key);
      state.mode = 'partial';
    }
    
    this.state$$.next({ ...state });
  }
  
  deselect(row: TRow): void {
    const key = this.getRowKey(row);
    const state = this.currentState;
    
    if (state.mode === 'all') {
      state.excludedIds.add(key);
    } else {
      state.selectedIds.delete(key);
      
      if (state.selectedIds.size === 0) {
        state.mode = 'none';
      }
    }
    
    this.state$$.next({ ...state });
  }
  
  selectAll(): void {
    this.state$$.next({
      mode: 'all',
      selectedIds: new Set(),
      excludedIds: new Set(),
    });
  }
  
  clearSelection(): void {
    this.state$$.next({
      mode: 'none',
      selectedIds: new Set(),
      excludedIds: new Set(),
    });
  }
  
  isSelected(row: TRow): boolean {
    const key = this.getRowKey(row);
    const state = this.currentState;
    
    if (state.mode === 'all') {
      return !state.excludedIds.has(key);
    }
    
    return state.selectedIds.has(key);
  }
  
  getSelectedCount(totalCount: number): number {
    const state = this.currentState;
    
    if (state.mode === 'all') {
      return totalCount - state.excludedIds.size;
    }
    
    return state.selectedIds.size;
  }
  
  getBulkOperationPayload(): BulkOperationPayload {
    const state = this.currentState;
    
    return {
      isAll: state.mode === 'all',
      selectedIds: state.mode === 'all' ? [] : Array.from(state.selectedIds),
      excludedIds: state.mode === 'all' ? Array.from(state.excludedIds) : [],
    };
  }
  
  private getRowKey(row: TRow): SelectionKey {
    if (!this.rowIdField) {
      throw new Error('TableSelectionService not configured. Call configure() first.');
    }
    
    const key = row[this.rowIdField];
    
    if (key === undefined || key === null) {
      throw new Error(`Row ID field '${String(this.rowIdField)}' is undefined or null`);
    }
    
    return key as SelectionKey;
  }
}
